import { useState } from "react";
import { Loader2, X } from "lucide-react";

import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  type BorradorReceta,
  type Periodicidad,
  useBorradorReceta,
  useCrearReceta,
} from "@/hooks/useRecetas";

export const DIAS = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];

export function usd(n: number | null | undefined): string {
  return n == null ? "—" : `US$${n.toFixed(4)}`;
}

/** Arma la periodicidad del formulario. */
export function periodicidadDe(
  tipo: Periodicidad["tipo"],
  hora: string,
  diaSemana: number,
  diaMes: number,
  fecha: string,
): Periodicidad {
  if (tipo === "una_vez") return { tipo, fecha, hora };
  if (tipo === "semanal") return { tipo, dia_semana: diaSemana, hora };
  if (tipo === "mensual") return { tipo, dia_mes: diaMes, hora };
  return { tipo: "diario", hora };
}

function mensaje(err: unknown): string {
  return err instanceof ApiError ? err.message : "No se pudo completar.";
}

/**
 * "Guardar y programar": 1) AROC convierte el turno en un reporte programado (una sola
 * llamada al modelo, con su costo); 2) el admin revisa pasos y columnas con
 * una muestra de filas; 3) elige cada cuándo y a dónde, y guarda.
 */
export function GuardarRecetaDialog({
  sessionId,
  documentoId,
  tituloSugerido,
  onClose,
}: {
  sessionId: string;
  documentoId: string;
  tituloSugerido: string;
  onClose: () => void;
}) {
  const borrador = useBorradorReceta();
  const crear = useCrearReceta();
  const [b, setB] = useState<BorradorReceta | null>(null);
  const [nombre, setNombre] = useState(tituloSugerido);
  const [tipo, setTipo] = useState<Periodicidad["tipo"]>("semanal");
  const [hora, setHora] = useState("08:00");
  const [diaSemana, setDiaSemana] = useState(0);
  const [diaMes, setDiaMes] = useState(1);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [resumen, setResumen] = useState(false);
  const [maxDia, setMaxDia] = useState(3);
  const [listo, setListo] = useState(false);

  const preparar = () =>
    borrador.mutate({ session_id: sessionId, documento_id: documentoId }, { onSuccess: setB });

  const guardar = () => {
    if (!b) return;
    crear.mutate(
      {
        nombre,
        spec: b.spec,
        periodicidad: periodicidadDe(tipo, hora, diaSemana, diaMes, fecha),
        destino: "telegram",
        resumen_llm: resumen,
        max_corridas_dia: maxDia,
        origen_session_id: sessionId,
        costo_platica_usd: b.costo_platica_usd,
        costo_borrador_usd: b.costo_borrador_usd,
      },
      { onSuccess: () => setListo(true) },
    );
  };

  return (
    <div role="dialog" aria-label="Guardar y programar" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg border bg-background p-5 shadow-lg">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Guardar y programar</h2>
          <button aria-label="Cerrar" onClick={onClose}>
            <X className="size-5" />
          </button>
        </div>

        {listo ? (
          <div className="space-y-3 text-sm">
            <p>Reporte programado guardado. Corre solo según lo que elegiste y te llega por Telegram; adminístralo en «Reportes programados».</p>
            <Button onClick={onClose}>Listo</Button>
          </div>
        ) : !b ? (
          <div className="space-y-3 text-sm">
            <p>
              AROC va a convertir este reporte en un reporte programado que se repite <strong>sin consultar a la IA</strong>:
              las mismas consultas con fechas relativas y el mismo formato. Para armarla hace <strong>una sola</strong>{" "}
              consulta corta (su costo se muestra).
            </p>
            {borrador.isError && <p className="text-destructive">{mensaje(borrador.error)}</p>}
            <Button onClick={preparar} disabled={borrador.isPending}>
              {borrador.isPending && <Loader2 className="mr-1 size-4 animate-spin" />}
              Preparar reporte programado
            </Button>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <section>
              <h3 className="mb-1 font-medium">Qué consulta</h3>
              <ul className="list-disc pl-5 font-mono text-xs">
                {b.spec.pasos.map((p) => (
                  <li key={p.id}>
                    {p.tool} {JSON.stringify(p.params)}
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h3 className="mb-1 font-medium">Qué entrega ({b.spec.formato === "xlsx" ? "Excel" : "Word"}: {b.spec.titulo})</h3>
              {b.muestra.map((s, i) => (
                <div key={i} className="mb-2">
                  <div className="text-xs font-semibold">{s.titulo}</div>
                  {s.columnas.length > 0 && (
                    <table className="mt-1 w-full border text-xs">
                      <thead>
                        <tr>
                          {s.columnas.map((c) => (
                            <th key={c} className="border bg-muted px-1 text-left">
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {s.filas.map((f, j) => (
                          <tr key={j}>
                            {f.map((c, k) => (
                              <td key={k} className="border px-1">
                                {c}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
              <p className="text-xs text-muted-foreground">
                Muestra con los datos de esta plática. Armar el reporte programado costó {usd(b.costo_borrador_usd)}; la plática
                original, {usd(b.costo_platica_usd)}. Cada corrida: US$0 (o una consulta corta si pides resumen).
              </p>
            </section>
            <section className="grid grid-cols-2 gap-3">
              <label className="col-span-2">
                Nombre
                <input className="mt-1 w-full rounded border px-2 py-1" value={nombre} onChange={(e) => setNombre(e.target.value)} />
              </label>
              <label>
                Cada cuándo
                <select className="mt-1 w-full rounded border px-2 py-1" value={tipo} onChange={(e) => setTipo(e.target.value as Periodicidad["tipo"])}>
                  <option value="una_vez">Una vez</option>
                  <option value="diario">Diario</option>
                  <option value="semanal">Semanal</option>
                  <option value="mensual">Mensual</option>
                </select>
              </label>
              <label>
                Hora (centro)
                <input type="time" className="mt-1 w-full rounded border px-2 py-1" value={hora} onChange={(e) => setHora(e.target.value)} />
              </label>
              {tipo === "semanal" && (
                <label>
                  Día
                  <select className="mt-1 w-full rounded border px-2 py-1" value={diaSemana} onChange={(e) => setDiaSemana(Number(e.target.value))}>
                    {DIAS.map((d, i) => (
                      <option key={d} value={i}>
                        {d}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {tipo === "mensual" && (
                <label>
                  Día del mes (1-28)
                  <input type="number" min={1} max={28} className="mt-1 w-full rounded border px-2 py-1" value={diaMes} onChange={(e) => setDiaMes(Number(e.target.value))} />
                </label>
              )}
              {tipo === "una_vez" && (
                <label>
                  Fecha
                  <input type="date" className="mt-1 w-full rounded border px-2 py-1" value={fecha} onChange={(e) => setFecha(e.target.value)} />
                </label>
              )}
              <label>
                Destino
                <select className="mt-1 w-full rounded border px-2 py-1" value="telegram" disabled>
                  <option value="telegram">Mi Telegram</option>
                  <option value="correo">Correo (cuando exista aroc@)</option>
                </select>
              </label>
              <label>
                Máximo de corridas por día
                <input type="number" min={1} max={24} className="mt-1 w-full rounded border px-2 py-1" value={maxDia} onChange={(e) => setMaxDia(Number(e.target.value))} />
              </label>
              <label className="col-span-2 flex items-center gap-2">
                <input type="checkbox" checked={resumen} onChange={(e) => setResumen(e.target.checked)} />
                Agregar resumen redactado por AROC (una consulta corta por corrida)
              </label>
            </section>
            {crear.isError && <p className="text-destructive">{mensaje(crear.error)}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button onClick={guardar} disabled={crear.isPending || !nombre.trim()}>
                {crear.isPending && <Loader2 className="mr-1 size-4 animate-spin" />}
                Guardar y programar
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
